import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { useGameStore } from './core/store';
import { useIsMobile } from './core/useIsMobile';
import { attachFullscreenRepair } from './core/fullscreen';
import { GameCanvas } from './scene/GameCanvas';
import { ExitBlackMonologue } from './systems/tunnel/ExitBlackMonologue';
import { useTunnelExitStore } from './systems/tunnel/tunnelExitStore';
import { CharacterCreateUI } from './systems/tunnel/CharacterCreateUI';
import { TunnelHUD } from './systems/tunnel/TunnelHUD';
import { TunnelHUDMobile } from './systems/tunnel/TunnelHUDMobile';
import { RunHUD } from './systems/ui/RunHUD';
import { RunHUDMobile } from './systems/ui/RunHUDMobile';
import { PackReveal } from './systems/ui/PackReveal';
import { ArenaOverlay } from './systems/ui/ArenaOverlay';
import { CinematicReveal } from './systems/cinematic/CinematicReveal';
import { StadiumBeat } from './systems/stadium/StadiumBeat';
import { SiteChrome } from './systems/ui/SiteChrome';
import { SiteChromeMobile } from './systems/ui/SiteChromeMobile';
import { DebugHud, isDebugEnabled } from './systems/ui/DebugHud';
import { LockerExperience } from './systems/locker/LockerExperience';
import { MatchIntroShell } from './systems/ui/MatchIntroShell';
import { MatchIntroMobile } from './systems/ui/MatchIntroMobile';
import { preloadGame } from './systems/boot/preload';
import {
  unlockAudio,
  preloadAudio,
  startIntroBed,
} from './systems/audio';
import './styles/global.css';
import './styles/loading.css';
import './systems/locker/locker.css';

/** Penalty stack — deferred until shot phase (prewarmed during tunnel exit / black). */
const ShotAim = lazy(() =>
  import('./systems/shot/ShotAim').then((m) => ({ default: m.ShotAim })),
);

/** Final scroll site — Motion + Lenis; only needed at reveal. */
const EndingPortfolio = lazy(() =>
  import('./systems/ui/ending/EndingPortfolio').then((m) => ({
    default: m.EndingPortfolio,
  })),
);
/**
 * Locker → tunnel: short iris wipe (not a long flat black hold).
 */
function TunnelRevealFade() {
  const phase = useGameStore((s) => s.phase);
  const setOverlayOpacity = useGameStore((s) => s.setOverlayOpacity);

  useEffect(() => {
    if (phase !== 'tunnel') return;
    const start = useGameStore.getState().overlayOpacity;
    if (start < 0.4) return;

    const el = document.querySelector('.fade-overlay') as HTMLElement | null;
    el?.classList.add('is-tunnel-iris');
    el?.style.setProperty('--iris', '0');

    const proxy = { iris: 0, o: 1 };
    // Slow cinematic aperture (~5s) while tunnel is already warm underneath
    const tween = gsap.to(proxy, {
      iris: 1,
      o: 0,
      delay: 0.55,
      duration: 4.45,
      ease: 'power2.inOut',
      onUpdate: () => {
        el?.style.setProperty('--iris', String(proxy.iris));
        // Keep overlay solid early; soft edge dissolve late
        setOverlayOpacity(proxy.iris < 0.88 ? 1 : 1 - (proxy.iris - 0.88) / 0.12);
      },
      onComplete: () => {
        setOverlayOpacity(0);
        el?.classList.remove('is-tunnel-iris');
        el?.style.removeProperty('--iris');
      },
    });

    return () => {
      tween.kill();
      el?.classList.remove('is-tunnel-iris');
      el?.style.removeProperty('--iris');
    };
  }, [phase, setOverlayOpacity]);

  return null;
}
function SoundHint({ mobile }: { mobile?: boolean }) {
  if (mobile) {
    return (
      <p className="mim__hint">Best experienced with headphones on</p>
    );
  }
  return (
    <p className="match-intro__hint">Best experienced with headphones on</p>
  );
}

function LoadingScreen() {
  const mobile = useIsMobile();
  const finishLoading = useGameStore((s) => s.finishLoading);
  const root = useRef<HTMLDivElement>(null);
  const done = useRef(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const complete = () => {
      if (done.current) return;
      done.current = true;
      const el = root.current;
      const go = () => finishLoading();
      if (el) {
        gsap.to(el, {
          opacity: 0,
          duration: 0.55,
          ease: 'power2.in',
          onComplete: go,
        });
        window.setTimeout(go, 900);
      } else {
        go();
      }
    };

    const tryStartBed = () => {
      void unlockAudio()
        .then(() => preloadAudio())
        .then(() => startIntroBed());
    };
    window.addEventListener('pointerdown', tryStartBed, { once: true });
    window.addEventListener('keydown', tryStartBed, { once: true });

    if (new URLSearchParams(location.search).get('skip')) {
      void preloadGame(() => {});
      done.current = true;
      finishLoading();
      return () => {
        window.removeEventListener('pointerdown', tryStartBed);
        window.removeEventListener('keydown', tryStartBed);
      };
    }

    const failsafe = window.setTimeout(complete, 12000);

    void preloadGame((p) => {
      setProgress(p.progress);
    }).then(complete);

    return () => {
      window.clearTimeout(failsafe);
      window.removeEventListener('pointerdown', tryStartBed);
      window.removeEventListener('keydown', tryStartBed);
    };
  }, [finishLoading]);

  const Shell = mobile ? MatchIntroMobile : MatchIntroShell;

  return (
    <div ref={root} style={{ position: 'absolute', inset: 0 }}>
      <Shell progress={progress} showLoadBar>
        {mobile ? (
          <div className="mim__ready" aria-hidden>
            <span className="mim__ready-label">Initializing</span>
            <span className="mim__ready-time">
              {String(Math.round(progress * 100)).padStart(2, '0')}%
            </span>
          </div>
        ) : (
          <div className="match-intro__ready" aria-hidden>
            <span className="match-intro__ready-label">Initializing</span>
            <span className="match-intro__ready-time">
              {String(Math.round(progress * 100)).padStart(2, '0')}%
            </span>
          </div>
        )}
        <SoundHint mobile={mobile} />
      </Shell>
    </div>
  );
}

function LandingScreen() {
  const mobile = useIsMobile();
  const startExperience = useGameStore((s) => s.startExperience);
  const finishEnding = useGameStore((s) => s.finishEnding);
  const root = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(3);
  const [ready, setReady] = useState(false);
  const starting = useRef(false);

  useEffect(() => {
    if (count <= 0) {
      setReady(true);
      return;
    }
    const id = window.setTimeout(() => setCount((c) => c - 1), 900);
    return () => window.clearTimeout(id);
  }, [count]);

  const go = async (mode: 'game' | 'site') => {
    if (starting.current || !ready) return;
    starting.current = true;
    await unlockAudio();
    await preloadAudio();
    if (mode === 'game') {
      await startIntroBed();
    }

    const el = root.current;
    const next = () => {
      if (mode === 'game') startExperience();
      else finishEnding();
    };

    if (el) {
      gsap.to(el, {
        opacity: 0,
        duration: 0.45,
        ease: 'power2.in',
        onComplete: next,
      });
    } else {
      next();
    }
  };

  useEffect(() => {
    if (!ready) return;
    // Warm portfolio chunk so "Portfolio Only" opens instantly
    void import('./systems/ui/ending/EndingPortfolio');
  }, [ready]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target;
      if (
        t instanceof HTMLElement &&
        (t.closest('button, a, input, textarea, select') || t.isContentEditable)
      ) {
        return;
      }
      if (e.repeat) return;
      if (e.key === 'Enter' && ready) void go('game');
      if ((e.key === 'p' || e.key === 'P') && ready) void go('site');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, mobile]);

  const Shell = mobile ? MatchIntroMobile : MatchIntroShell;

  const pathChoice = mobile ? (
    <div className="mim__paths">
      <p className="mim__paths-label">— Choose your path —</p>
      <button
        type="button"
        className="mim__cta"
        onClick={() => void go('game')}
      >
        <span className="mim__cta-main">Full Experience →</span>
        <span className="mim__cta-sub">Locker · Tunnel · Penalty · Site</span>
      </button>
      <button
        type="button"
        className="mim__cta mim__cta--ghost"
        onClick={() => void go('site')}
      >
        <span className="mim__cta-main">Portfolio Only →</span>
        <span className="mim__cta-sub">Just the Portfolio</span>
      </button>
    </div>
  ) : (
    <div className="match-intro__paths">
      <p className="match-intro__paths-label">— Choose your path —</p>
      <div className="match-intro__paths-row">
        <button
          type="button"
          className="match-intro__cta match-intro__cta--path"
          onClick={() => void go('game')}
        >
          <span className="match-intro__cta-main">Full Experience →</span>
          <span className="match-intro__cta-sub">
            Locker · Tunnel · Penalty · Portfolio
          </span>
        </button>
        <button
          type="button"
          className="match-intro__cta match-intro__cta--path match-intro__cta--ghost"
          onClick={() => void go('site')}
        >
          <span className="match-intro__cta-main">Portfolio Only →</span>
          <span className="match-intro__cta-sub">Press P · Just the Portfolio</span>
        </button>
      </div>
    </div>
  );

  return (
    <div ref={root} style={{ position: 'absolute', inset: 0 }}>
      <Shell showLoadBar={false}>
        {!ready ? (
          mobile ? (
            <div className="mim__ready">
              <span className="mim__ready-label">Match Ready</span>
              <span className="mim__ready-time">
                00:0{Math.max(count, 1)}
              </span>
            </div>
          ) : (
            <div className="match-intro__ready">
              <span className="match-intro__ready-label">Match Ready</span>
              <span className="match-intro__ready-time">
                00:0{Math.max(count, 1)}
              </span>
            </div>
          )
        ) : (
          pathChoice
        )}
        <SoundHint mobile={mobile} />
      </Shell>
    </div>
  );
}

export default function App() {
  const mobile = useIsMobile();
  const phase = useGameStore((s) => s.phase);
  const overlayOpacity = useGameStore((s) => s.overlayOpacity);
  const tunnelWalking = useGameStore((s) => s.tunnelWalking);
  const tunnelExitBeat = useTunnelExitStore((s) => s.beat);

  useEffect(() => {
    attachFullscreenRepair();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const skip = params.get('skip');
    if (!skip) return;
    const apply = () => {
      const s = useGameStore.getState();
      if (skip === 'run') {
        s.startRun();
        return;
      }
      if (skip === 'locker') {
        s.startExperience();
        return;
      }
      if (skip === 'tunnel') {
        s.enterTunnelFromLocker();
        return;
      }
      if (skip === 'shot') s.startShot();
      else if (skip === 'keeper') {
        s.startShot();
      } else if (skip === 'cinematic') {
        useGameStore.setState({
          phase: 'cinematic',
          overlayOpacity: 0,
          shotSide: 1,
          keeperDiveSide: -1,
        });
      } else if (skip === 'stadium') s.finishCinematic();
      else if (skip === 'reveal') {
        useGameStore.setState({ phase: 'reveal', overlayOpacity: 0 });
      }
    };
    const id = window.setTimeout(apply, skip === 'run' ? 100 : 50);
    return () => window.clearTimeout(id);
  }, [typeof location !== 'undefined' ? location.search : '']);

  const tunnelWarming = useGameStore((s) => s.tunnelWarming);
  const showMainCanvas = phase === 'tunnel' || phase === 'run' || tunnelWarming;

  return (
    <div className="app-shell">
      <TunnelRevealFade />
      {showMainCanvas && <GameCanvas />}

      <div className="ui-layer">
        {phase === 'loading' && <LoadingScreen />}
        {phase === 'landing' && <LandingScreen />}
        {phase === 'locker' && <LockerExperience />}
        {phase === 'tunnel' && !tunnelWalking && <CharacterCreateUI />}
        {phase === 'tunnel' &&
          tunnelWalking &&
          tunnelExitBeat !== 'fade' &&
          (mobile ? <TunnelHUDMobile /> : <TunnelHUD />)}
        {phase === 'run' && (
          <>
            {mobile ? <RunHUDMobile /> : <RunHUD />}
            <PackReveal />
            <ArenaOverlay />
          </>
        )}
        {phase === 'shot' && (
          <Suspense fallback={null}>
            <ShotAim />
          </Suspense>
        )}
        {phase === 'cinematic' && <CinematicReveal />}
        {phase === 'stadium' && <StadiumBeat />}
        {phase === 'reveal' && (
          <Suspense fallback={null}>
            <EndingPortfolio />
          </Suspense>
        )}
      </div>

      {mobile ? <SiteChromeMobile /> : <SiteChrome />}

      {isDebugEnabled() && <DebugHud />}

      {phase !== 'loading' && phase !== 'landing' && (
        <div
          className="fade-overlay"
          style={{ opacity: overlayOpacity }}
          aria-hidden
        />
      )}
      <ExitBlackMonologue />
    </div>
  );
}
